import { authApi } from '../auth/api';
import { endConference } from '../base/conference/actions.any';
import { isLocalParticipantModerator } from '../base/participants/functions';
import { CLEAR_ROOM_TIME_LIMIT, HIDE_FINAL_COUNTDOWN, HIDE_ROOM_TIME_LIMIT_WARNING, SET_ROOM_START_TIME, SET_TIME_LIMIT_WARNING_SHOWN, SHOW_FINAL_COUNTDOWN, SHOW_ROOM_TIME_LIMIT_WARNING, UPDATE_COUNTDOWN_SECONDS } from './actionTypes';
import { ROOM_TIME_LIMIT_MS, ROOM_TIME_WARNING_REMAINING_MS } from './constants';
import logger from './logger';
const ROOM_START_TIME_STORAGE_KEY = 'didar_roomStartTime';
/**
 * Stores the room start time from the enterRoom API response.
 *
 * @param {string} roomStartTime - The UTC timestamp of when the room started.
 * @returns {Object}
 */
export function setRoomStartTime(roomStartTime) {
    console.log('💾 [ROOM TIME LIMIT] setRoomStartTime called with:', roomStartTime);
    try {
        localStorage.setItem(ROOM_START_TIME_STORAGE_KEY, roomStartTime);
    }
    catch {
        // ignore storage errors
    }
    return {
        type: SET_ROOM_START_TIME,
        roomStartTime
    };
}
/**
 * Clears all room time limit state.
 *
 * @returns {Object}
 */
export function clearRoomTimeLimit() {
    try {
        localStorage.removeItem(ROOM_START_TIME_STORAGE_KEY);
    }
    catch {
        // ignore storage errors
    }
    return {
        type: CLEAR_ROOM_TIME_LIMIT,
    };
}
/**
 * Marks that the time limit warning has been shown.
 *
 * @returns {Object}
 */
export function setTimeLimitWarningShown() {
    return {
        type: SET_TIME_LIMIT_WARNING_SHOWN,
    };
}
/**
 * Shows the room time limit warning notification.
 *
 * @returns {Object}
 */
export function showRoomTimeLimitWarning() {
    return {
        type: SHOW_ROOM_TIME_LIMIT_WARNING,
    };
}
/**
 * Hides the room time limit warning notification.
 *
 * @returns {Object}
 */
export function hideRoomTimeLimitWarning() {
    return {
        type: HIDE_ROOM_TIME_LIMIT_WARNING
    };
}
/**
 * Shows the final countdown.
 *
 * @param {number} seconds - Number of seconds remaining.
 * @returns {Object}
 */
export function showFinalCountdown(seconds) {
    return {
        type: SHOW_FINAL_COUNTDOWN,
        seconds
    };
}
/**
 * Hides the final countdown.
 *
 * @returns {Object}
 */
export function hideFinalCountdown() {
    return {
        type: HIDE_FINAL_COUNTDOWN
    };
}
/**
 * Updates countdown seconds.
 *
 * @param {number} seconds - Number of seconds remaining.
 * @returns {Object}
 */
export function updateCountdownSeconds(seconds) {
    return {
        type: UPDATE_COUNTDOWN_SECONDS,
        seconds
    };
}
/**
 * Starts the room time limit timers. Called from middleware when CONFERENCE_JOINED fires.
 * Sets up two timers.
 *   1. Warning timer (5 min remaining) - shows notification and dialog.
 *   2. Exit timer (at 60 min elapsed) - moderator calls exitAll, all get endConference.
 *
 * @returns {Function}
 */
export function startRoomTimeLimitTimers() {
    return (dispatch, getState) => {
        const { roomStartTime } = getState()['features/room-time-limit'];
        console.log('🚀 [ROOM TIME LIMIT] startRoomTimeLimitTimers called');
        console.log('📅 [ROOM TIME LIMIT] roomStartTime from state:', roomStartTime);
        if (!roomStartTime) {
            console.error('❌ [ROOM TIME LIMIT] No roomStartTime found in state!');
            logger.error('No roomStartTime found in state');
            return;
        }
        console.log('🚀 [ROOM TIME LIMIT] Starting timers...');
        const startTime = new Date(roomStartTime).getTime();
        const now = Date.now();
        const elapsed = now - startTime;
        console.log('⏰ [ROOM TIME LIMIT] Initial time check:');
        console.log('  - roomStartTime string:', roomStartTime);
        console.log('  - Start time (timestamp):', startTime, new Date(startTime).toISOString());
        console.log('  - Current time (timestamp):', now, new Date(now).toISOString());
        console.log('  - Elapsed time:', Math.floor(elapsed / 1000), 'seconds (', Math.floor(elapsed / 60000), 'minutes )');
        // اگر زمان API در آینده است (elapsed منفی)، از زمان فعلی استفاده کن
        let actualStartTime = startTime;
        if (elapsed < 0) {
            console.warn('⚠️ [ROOM TIME LIMIT] roomStartTime is in the future! Using current time as start time.');
            actualStartTime = now;
        }
        const actualElapsed = now - actualStartTime;
        // Warning when ROOM_TIME_WARNING_REMAINING_MS remains (5 minutes before end)
        const warningAt = ROOM_TIME_LIMIT_MS - ROOM_TIME_WARNING_REMAINING_MS;
        const timeUntilWarning = warningAt - actualElapsed;
        // Limit at 5 minutes (300 seconds)
        const timeUntilLimit = ROOM_TIME_LIMIT_MS - actualElapsed;
        console.log('⏰ [ROOM TIME LIMIT] Corrected time calculations:');
        console.log('  - Actual start time:', new Date(actualStartTime).toISOString());
        console.log('  - Actual elapsed time:', Math.floor(actualElapsed / 1000), 'seconds (', Math.floor(actualElapsed / 60000), 'minutes )');
        console.log('  - Time until warning (5 min remaining):', Math.floor(timeUntilWarning / 1000), 'seconds (', Math.floor(timeUntilWarning / 60000), 'minutes )');
        console.log('  - Time until limit (60 min):', Math.floor(timeUntilLimit / 1000), 'seconds (', Math.floor(timeUntilLimit / 60000), 'minutes )');
        logger.info(`Room started at ${new Date(actualStartTime).toISOString()}, elapsed: ${actualElapsed}ms (${Math.floor(actualElapsed / 1000)}s), ` +
            `time until warning: ${timeUntilWarning}ms (${Math.floor(timeUntilWarning / 1000)}s), ` +
            `time until limit: ${timeUntilLimit}ms (${Math.floor(timeUntilLimit / 1000)}s)`);
        // If time limit already passed, end immediately
        if (timeUntilLimit <= 0) {
            console.error('⚠️ [ROOM TIME LIMIT] Time limit already passed! Ending conference now.');
            logger.info('Room time limit already reached, ending conference.');
            dispatch(_handleTimeLimitReached());
            return;
        }
        // Set warning timer (at 3 minutes)
        if (timeUntilWarning > 0) {
            console.log(`⏳ [ROOM TIME LIMIT] Setting warning timer for ${Math.floor(timeUntilWarning / 1000)} seconds (${Math.floor(timeUntilWarning / 60000)} minutes)`);
            const warningTimerId = window.setTimeout(() => {
                console.log('🔔🔔🔔 [ROOM TIME LIMIT] ===== WARNING TIMER FIRED! ===== (5 minutes remaining)');
                dispatch(_handleTimeLimitWarning());
            }, timeUntilWarning);
            window.__roomTimeLimitWarningTimer = warningTimerId;
            console.log('✅ [ROOM TIME LIMIT] Warning timer ID:', warningTimerId);
        }
        else {
            // Warning time already passed, show immediately
            console.log('⚠️ [ROOM TIME LIMIT] Warning time already passed, showing immediately');
            dispatch(_handleTimeLimitWarning());
        }
        // Set exit timer (at 5 minutes)
        console.log(`⏳ [ROOM TIME LIMIT] Setting exit timer for ${Math.floor(timeUntilLimit / 1000)} seconds (${Math.floor(timeUntilLimit / 60000)} minutes)`);
        const exitTimerId = window.setTimeout(() => {
            console.log('🚪🚪🚪 [ROOM TIME LIMIT] ===== EXIT TIMER FIRED! ===== (60 minutes elapsed) - Ending conference');
            dispatch(_handleTimeLimitReached());
        }, timeUntilLimit);
        window.__roomTimeLimitExitTimer = exitTimerId;
        console.log('✅ [ROOM TIME LIMIT] Exit timer ID:', exitTimerId);
        // Set countdown timer (10 seconds before limit)
        const timeUntilCountdown = timeUntilLimit - 10000; // 10 seconds before limit
        if (timeUntilCountdown > 0) {
            console.log(`⏳ [ROOM TIME LIMIT] Setting countdown timer for ${Math.floor(timeUntilCountdown / 1000)} seconds`);
            const countdownTimerId = window.setTimeout(() => {
                console.log('⏱️ [ROOM TIME LIMIT] ===== COUNTDOWN TIMER FIRED! ===== (10 seconds remaining)');
                dispatch(showFinalCountdown(10));
            }, timeUntilCountdown);
            window.__roomTimeLimitCountdownTimer = countdownTimerId;
            console.log('✅ [ROOM TIME LIMIT] Countdown timer ID:', countdownTimerId);
        }
        else if (timeUntilLimit > 0 && timeUntilLimit <= 10000) {
            // Less than 10 seconds remaining, show countdown immediately
            const secondsRemaining = Math.ceil(timeUntilLimit / 1000);
            console.log(`⚠️ [ROOM TIME LIMIT] Less than 10 seconds remaining (${secondsRemaining}s), showing countdown immediately`);
            dispatch(showFinalCountdown(secondsRemaining));
        }
        console.log('✅ [ROOM TIME LIMIT] All timers set successfully');
        console.log('📊 [ROOM TIME LIMIT] Timer IDs stored in window:', {
            warning: window.__roomTimeLimitWarningTimer,
            exit: window.__roomTimeLimitExitTimer,
            countdown: window.__roomTimeLimitCountdownTimer
        });
    };
}
/**
 * Cleans up room time limit timers. Called when conference is left.
 *
 * @returns {Function}
 */
export function cleanupRoomTimeLimitTimers() {
    return () => {
        if (window.__roomTimeLimitWarningTimer) {
            window.clearTimeout(window.__roomTimeLimitWarningTimer);
            window.__roomTimeLimitWarningTimer = null;
        }
        if (window.__roomTimeLimitExitTimer) {
            window.clearTimeout(window.__roomTimeLimitExitTimer);
            window.__roomTimeLimitExitTimer = null;
        }
        if (window.__roomTimeLimitCountdownTimer) {
            window.clearTimeout(window.__roomTimeLimitCountdownTimer);
            window.__roomTimeLimitCountdownTimer = null;
        }
    };
}
/**
 * Shows the 5-minute warning notification and dialog.
 *
 * @returns {Function}
 */
function _handleTimeLimitWarning() {
    return (dispatch) => {
        console.log('🔔 [ROOM TIME LIMIT] Showing warning notification (5 minutes remaining)');
        logger.info('Showing room time limit warning (5 minutes remaining).');
        dispatch(setTimeLimitWarningShown());
        // فقط مودال سفارشی نمایش داده میشه، notification جیتسی نمیاد
        dispatch(showRoomTimeLimitWarning());
        console.log('✅ [ROOM TIME LIMIT] Warning notification dispatched');
    };
}
/**
 * Handles time limit reached: moderator calls exitAll, all end conference.
 *
 * @returns {Function}
 */
function _handleTimeLimitReached() {
    return async (dispatch, getState) => {
        console.log('🚪 [ROOM TIME LIMIT] Time limit reached! Ending conference...');
        logger.info('Room time limit reached.');
        const isModerator = isLocalParticipantModerator(getState());
        console.log('👤 [ROOM TIME LIMIT] Is moderator:', isModerator);
        // Only moderator calls exitAll API to kick everyone
        if (isModerator) {
            try {
                const roomName = window.location.pathname.slice(1).toLowerCase();
                if (roomName) {
                    console.log('📞 [ROOM TIME LIMIT] Moderator calling exitAll API for room:', roomName);
                    logger.info('Moderator calling exitAll for room:', roomName);
                    const response = await authApi.exitAll(roomName);
                    console.log('✅ [ROOM TIME LIMIT] exitAll API response:', response);
                    console.log('✅ [ROOM TIME LIMIT] exitAll API call successful - all participants will be kicked by server');
                }
                else {
                    console.warn('⚠️ [ROOM TIME LIMIT] No room name found, cannot call exitAll');
                }
            }
            catch (e) {
                console.error('❌ [ROOM TIME LIMIT] exitAll API failed:', e);
                logger.warn('exitAll API failed on time limit reached', e);
            }
        }
        else {
            console.log('👥 [ROOM TIME LIMIT] Not moderator, only ending local conference');
        }
        // Everyone ends their local conference
        console.log('🔚 [ROOM TIME LIMIT] Dispatching endConference for local participant');
        dispatch(endConference());
    };
}
